import type { StateCreator } from 'zustand'
import { apiService } from '@/service/api'
import {
  FEATURE_KEYS,
  type FeatureKey,
} from '@/features/featureAccess/features'

/** A forced reload within this long of the last one is skipped. */
export const FEATURE_ACCESS_REFRESH_MIN_MS = 5_000

/**
 * The areas this person may use, from `GET /v2/me/features`. Until it loads
 * (or if it fails) every area is treated as on, matching the backend default.
 */
export interface FeatureAccessState {
  /** Features for `featureAccessUserId`; null = not loaded for the current user. */
  enabledFeatures: FeatureKey[] | null
  featureAccessUserId: string | null
  featureAccessLoading: boolean
  /** The user the in-flight (or last) request is for; a newer request supersedes it. */
  featureAccessRequestedFor: string | null
  featureAccessLoadedAt: number
  /** `force` reloads even when this user's list is already loaded. */
  loadFeatureAccess: (userId: string, force?: boolean) => Promise<void>
  /** Forget the loaded list (it depends on the active organisation). */
  clearFeatureAccess: () => void
}

export const featureAccessSlice: StateCreator<FeatureAccessState> = (
  set,
  get
) => {
  // At most one delayed retry for a forced reload that arrived too soon.
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  // Bumped by each request and by clearFeatureAccess: only the latest answer lands,
  // so a list requested under the previous organisation never overwrites a newer one.
  let generation = 0
  return {
    enabledFeatures: null,
    featureAccessUserId: null,
    featureAccessLoading: false,
    featureAccessRequestedFor: null,
    featureAccessLoadedAt: 0,

    loadFeatureAccess: async (userId: string, force = false) => {
      const state = get()
      if (
        state.featureAccessLoading &&
        state.featureAccessRequestedFor === userId
      )
        return
      if (!state.featureAccessLoading && state.featureAccessUserId === userId) {
        if (!force) return
        const wait =
          FEATURE_ACCESS_REFRESH_MIN_MS -
          (Date.now() - state.featureAccessLoadedAt)
        if (wait > 0) {
          // Too soon to reload, but the list may be stale: try again once the wait is over.
          if (!retryTimer) {
            retryTimer = setTimeout(() => {
              retryTimer = null
              void get().loadFeatureAccess(userId, true)
            }, wait)
          }
          return
        }
      }
      set({ featureAccessLoading: true, featureAccessRequestedFor: userId })
      const thisRequest = ++generation
      let features: FeatureKey[] = [...FEATURE_KEYS]
      try {
        const response = await apiService.getMyFeatures()
        features = response.features.filter((f): f is FeatureKey =>
          (FEATURE_KEYS as readonly string[]).includes(f)
        )
      } catch {
        // Fail open, like the backend: an outage never hides the product.
      }
      // A request for another user (session switch) started meanwhile: its answer wins.
      if (get().featureAccessRequestedFor !== userId || thisRequest !== generation) return
      set({
        enabledFeatures: features,
        featureAccessUserId: userId,
        featureAccessLoading: false,
        featureAccessLoadedAt: Date.now(),
      })
    },

    clearFeatureAccess: () => {
      generation++
      set({
        enabledFeatures: null,
        featureAccessUserId: null,
        featureAccessLoading: false,
        featureAccessRequestedFor: null,
        featureAccessLoadedAt: 0,
      })
    },
  }
}
